/*
 * @Author: Ryan
 * @Date: 2025-12-26
 * @Description: Old Way vs FalcoCut comparison section
 */
import React from 'react';

import StartButton from './StartButton';

interface ComparisonItem {
  label: string;
  value: string;
}

const oldWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: '40–60 hours' },
  { label: 'Cost of Production', value: '$1,000+ for actors, studio, and editing' },
  { label: 'Team Required', value: 'Scriptwriter, videographer, editor, presenter' },
  { label: 'Quality', value: 'Inconsistent and amateur' },
  {
    label: 'Workflow',
    value:
      'Hiring actors, scheduling shoots, recording audio, editing, multiple reshoots',
  },
];

const newWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: '10 minutes' },
  { label: 'Cost of Production', value: 'From $15 per video' },
  { label: 'Team Required', value: 'Just you' },
  { label: 'Quality', value: 'Studio-grade, scalable, and consistent' },
  {
    label: 'Workflow',
    value: 'Pick an avatar → Type your script → Generate video instantly',
  },
];

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Skip the long hours, high costs, and messy workflows of traditional video
          shoots. With FalcoCut, you can create professional, studio-quality avatar videos
          in just minutes—no actors, cameras, or editing teams required.
        </p>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">•</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-gray-800">
              <span>😫</span>
              <span>Complex, expensive, and time-consuming.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#7C3AED] bg-[#7C3AED]/5 p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#7C3AED] mr-2">•</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-[#7C3AED]">
              <span>🚀</span>
              <span>Fast, affordable, and professional.</span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <StartButton text="Create Your AI Avatar Video" />
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
